import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  GeneralContainer,
  CustomTable,
  useCustomToast,
} from '../../../../components';
import { useNavigate } from 'react-router-dom';
import SearchFilters from '../components/SearchFilters';
import AdvancedFiltersView from './AdvancedFiltersView';
import { OnlineInventoryAPI } from '../../api/onlineInventoryAPI';
import { DollarCircleOutlined, ExclamationCircleOutlined, SyncOutlined, ToolOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import InventoryStatusExtra from './InventoryStatusExtra';
import { useAtom } from 'jotai';
import { dataForCreate, modalStateAtom } from '../../../../state/atoms';

interface OnlineInventoryViewProps {
  movements: Array<any>;
  isLoading: boolean;
  formikProps: any;
  refetch: () => void;
  onViewReport: (record: any) => void;
  onViewVerify: () => void;
}

const OnlineInventoryView: React.FC<OnlineInventoryViewProps> = ({
  movements,
  isLoading,
  formikProps,
  onViewReport,
  onViewVerify,
  refetch,
}) => {
  const { showToast } = useCustomToast();
  const { values, setFieldValue, handleSubmit, resetForm } = formikProps;
  const navigate = useNavigate();
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [filteredMovements, setFilteredMovements] = useState(movements);
  const [advancedFilters, setAdvancedFilters] = useState<
    Record<string, string>
  >({});
  const [, setFormInventory] = useAtom(dataForCreate);
  const [, setModalAtom] = useAtom<boolean>(modalStateAtom);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Abrir modal de filtros avanzados
  const handleOpenFilters = () => {
    setIsFilterModalVisible(true);
  };

  // Cerrar modal de filtros avanzados
  const handleCloseFilters = () => {
    setIsFilterModalVisible(false);
  };

  // Configurar selección de filas
  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[], newSelectedRows: any[]) => {
      setSelectedRowKeys(newSelectedRowKeys);
      setSelectedRows(newSelectedRows);
    },
  };

  const paginationConfig = {
    current: currentPage,
    pageSize: pageSize,
    total: filteredMovements.length,
    onChange: (page: number, size?: number) => {
      setCurrentPage(page);
      if (size) setPageSize(size);
    },
    showSizeChanger: true,
  };

  const handleAdvancedFilters = async (newFilters: any[]) => {
    try {
      const filtersToSend = newFilters.reduce(
        (acc, filter) => {
          acc[filter.value] = filter.searchValue;
          return acc;
        },
        {} as Record<string, string>
      );

      setAdvancedFilters(filtersToSend);
      const filteredData =
        await OnlineInventoryAPI.fetchFilteredMovements(filtersToSend);
      setFilteredMovements(filteredData);
      setIsFilterModalVisible(false);
    } catch (error) {
      console.error('Error aplicando filtros avanzados:', error);
    }
  };

  useEffect(() => {
    let filteredData = movements;
    if (values.search) {
      const searchValue = values.search.toLowerCase();
      filteredData = filteredData.filter(
        (item) =>
          item.inventory_code?.toLowerCase().includes(searchValue) ||
          item.plate_code?.toLowerCase().includes(searchValue) ||
          item.cost_center?.toLowerCase().includes(searchValue) ||
          item.asset_class?.code?.toLowerCase().includes(searchValue) ||
          item.location?.toLowerCase().includes(searchValue) ||
          item.description?.toLowerCase().includes(searchValue)
      );
    }

    if (Object.keys(advancedFilters).length > 0) {
      Object.entries(advancedFilters).forEach(([key, value]) => {
        filteredData = filteredData.filter((item) =>
          item[key]?.toLowerCase().includes(value.toLowerCase())
        );
      });
    }

    if (values.inventory_status) {
      filteredData = filteredData.filter(
        (item) =>
          item.status &&
          item.status.toLowerCase().includes(values?.inventory_status?.toLowerCase())
      );
    }

    if ((!values.search && !values.inventory_status) && Object.keys(advancedFilters).length === 0) {
      filteredData = movements;
    }

    setFilteredMovements(filteredData);
  }, [values, movements, advancedFilters]);

  const goToView = (record: any, path: string) => {
    const body: Record<string, any> = {
      responsible_id: movements[0]?.responsible_id,
    }

    if (typeof record[0] === 'number') {
      body['id_items'] = record;
    } else {
      body['item'] = record;
    }

    setFormInventory(body);
    setModalAtom(true);
    navigate(path);
  }

  const validateConcepts = (record: any) => {
    if (selectedRowKeys.length > 1) {
      showToast('error', 'Debe seleccionar solo un activo para solicitar conceptos técnicos.');
      return;
    }

    goToView(record, '/technicalconcept');
  }

  const columns: ColumnsType<any> = [
    {
      title: 'Código/Placa',
      dataIndex: 'plate_code',
      key: 'plate_code',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Placa alterna',
      dataIndex: 'plate_code_alternative',
      key: 'plate_code_alternative',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Responsable',
      dataIndex: 'responsible',
      key: 'responsible',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'observations',
      align: 'center',
      width: 300,
      render: (text: string) => {
        return (
          <Tooltip title={text || 'Sin registro'}>
            <div
              style={{
                maxWidth: 250,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                margin: '0 auto',
              }}
            >
              {text || 'Sin registro'}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Regional',
      dataIndex: 'regional',
      key: 'regional',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Seccional',
      dataIndex: 'sectional',
      key: 'sectional',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Dependencia',
      dataIndex: 'dependency',
      key: 'dependency',
      sorter: true,
      align: 'center',
    },
    {
      title: 'Estado de validación',
      dataIndex: 'inventory_status',
      key: 'inventory_status',
      sorter: true,
      align: 'center',
      render: (text: string | null) => (text ? text : 'No registrada'),
    },
    {
      title: 'Fecha de última verificación',
      dataIndex: 'last_verification_date',
      key: 'last_verification_date',
      sorter: true,
      align: 'center',
      render: (date: string | null) => (date ? date : 'No registrada'),
    },
    {
      title: 'Centro de costo',
      dataIndex: 'cost_center',
      key: 'cost_center',
      sorter: true,
      align: 'center',
      width: 300,
      render: (text: string) => {
        return (
          <Tooltip title={text || 'Sin registro'}>
            <div
              style={{
                maxWidth: 250,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                margin: '0 auto',
              }}
            >
              {text || 'Sin registro'}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      className: 'ml-16 mr-16',
      render: (_: any, record: any) => (
        <>
          <Tooltip title="Reintegro">
            <Button
              type="link"
              icon={<SyncOutlined />}
              onClick={() => {
                goToView(record, `/refunds`);
              }}
            />
          </Tooltip>
          <Tooltip title="Traslado entre funcionarios">
            <Button
              type="link"
              icon={<DollarCircleOutlined />}
              onClick={() => {
                goToView(record, `/transfers`);
              }}
            />
          </Tooltip>
          <Tooltip title="Solicitud de conceptos técnicos">
            <Button
              type="link"
              icon={<ToolOutlined />}
              onClick={() => {
                validateConcepts(record);
              }}
            />
          </Tooltip>
          <Tooltip title="Reportar novedad">
            <Button
              type="link"
              icon={<ExclamationCircleOutlined />}
              onClick={() => {
                onViewReport([record]);
              }}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <GeneralContainer
      title="Mi Inventario en línea"
      extra={
        <InventoryStatusExtra
          date_start={movements?.[0]?.start_date || 'N/A'}
          date_end={movements?.[0]?.end_date || 'N/A'}
          status={movements?.[0]?.general_status || 'N/A'}
          onHandleCheckInventory={() => onViewVerify()}
        />
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        <Col xs={24}>
          <SearchFilters
            formik={formikProps}
            isLoading={isLoading}
            onOpenFilters={handleOpenFilters}
            onCancel={() => {
              resetForm();
              setAdvancedFilters({});
              refetch();

              setTimeout(() => {
                setFilteredMovements(movements);
              }, 500);
            }}
          />
        </Col>
      </Row>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '0 10px 10px 10px',
        }}
      >
        <Checkbox
          onChange={(e) => {
            setSelectedRowKeys(
              e.target.checked ? movements.map((item) => item.id) : []
            )
            setSelectedRows(
              e.target.checked ? movements : []
            );
          }
          }
          checked={
            selectedRowKeys.length === movements.length && movements.length > 0
          }
        >
          Seleccionar todos
        </Checkbox>

        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            type="primary"
            onClick={() => goToView(selectedRowKeys, '/refunds')}
            disabled={selectedRowKeys.length === 0}
          >
            Reintegro
          </Button>
          <Button
            type="primary"
            onClick={() => goToView(selectedRowKeys, '/transfers')}
            disabled={selectedRowKeys.length === 0}
          >
            Traslado entre funcionarios
          </Button>
          <Button
            type="primary"
            onClick={() => onViewReport(selectedRows)}
            disabled={selectedRowKeys.length === 0}
          >
            Novedades
          </Button>
        </div>
      </div>

      <CustomTable
        columns={columns}
        dataSource={filteredMovements}
        loading={isLoading}
        rowKey="id"
        paginationConfig={paginationConfig}
        scroll={{ x: 'max-content' }}
        rowSelection={rowSelection}
        size="small"
      />

      {isFilterModalVisible && (
        <AdvancedFiltersView
          onClose={handleCloseFilters}
          onApplyFilters={handleAdvancedFilters}
        />
      )}
    </GeneralContainer>
  );
};

export default OnlineInventoryView;
